import { create } from 'zustand';
import {
  Conversation,
  ConversationCreateData,
  ConversationUpdateData,
  Message,
} from '@kifuerpflege/shared';
import * as conversationApi from '../features/conversations/api/conversationApi';
import { websocketService } from '../services/websocket';

interface StreamingMessage {
  conversationId: string;
  content: string;
  isStreaming: boolean;
}

interface ConversationState {
  // State
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Record<string, Message[]>;
  streamingMessage: StreamingMessage | null;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;

  // Actions
  loadConversations: () => Promise<void>;
  createConversation: (data: ConversationCreateData) => Promise<Conversation>;
  selectConversation: (id: string) => Promise<void>;
  updateConversation: (id: string, data: ConversationUpdateData) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  sendMessage: (conversationId: string, content: string) => void;
  addMessage: (conversationId: string, message: Message) => void;
  appendToStreamingMessage: (conversationId: string, content: string) => void;
  finalizeStreamingMessage: (conversationId: string, messageId: string, tokenCount: number) => void;
  updateConversationTitle: (conversationId: string, title: string) => void;
  clearActiveConversation: () => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const useConversationStore = create<ConversationState>((set, get) => ({
  // Initial state
  conversations: [],
  activeConversationId: null,
  messages: {},
  streamingMessage: null,
  isLoading: false,
  isSending: false,
  error: null,

  // Load all conversations
  loadConversations: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await conversationApi.listConversations();

      set({
        conversations: response.conversations,
        isLoading: false,
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to load conversations';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  // Create new conversation
  createConversation: async (data: ConversationCreateData) => {
    try {
      set({ isLoading: true, error: null });

      const conversation = await conversationApi.createConversation(data);

      set((state) => ({
        conversations: [conversation, ...state.conversations],
        activeConversationId: conversation.id,
        messages: { ...state.messages, [conversation.id]: [] },
        isLoading: false,
      }));

      return conversation;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to create conversation';
      set({
        error: errorMessage,
        isLoading: false,
      });
      throw error;
    }
  },

  // Select conversation and load messages
  selectConversation: async (id: string) => {
    try {
      set({ activeConversationId: id, error: null });

      // Load messages if not already loaded
      const { messages } = get();
      if (!messages[id]) {
        await get().loadMessages(id);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to select conversation';
      set({ error: errorMessage });
    }
  },

  // Update conversation
  updateConversation: async (id: string, data: ConversationUpdateData) => {
    try {
      set({ error: null });

      const updated = await conversationApi.updateConversation(id, data);

      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv.id === id ? updated : conv
        ),
      }));
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to update conversation';
      set({ error: errorMessage });
      throw error;
    }
  },

  // Delete conversation
  deleteConversation: async (id: string) => {
    try {
      set({ error: null });

      await conversationApi.deleteConversation(id);

      set((state) => {
        const newMessages = { ...state.messages };
        delete newMessages[id];

        return {
          conversations: state.conversations.filter((conv) => conv.id !== id),
          activeConversationId:
            state.activeConversationId === id ? null : state.activeConversationId,
          messages: newMessages,
        };
      });
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to delete conversation';
      set({ error: errorMessage });
      throw error;
    }
  },

  // Load messages for conversation
  loadMessages: async (conversationId: string) => {
    try {
      set({ isLoading: true, error: null });

      const response = await conversationApi.getConversationMessages(conversationId);

      set((state) => ({
        messages: {
          ...state.messages,
          [conversationId]: response.messages,
        },
        isLoading: false,
      }));
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || 'Failed to load messages';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  // Send message via WebSocket
  sendMessage: (conversationId: string, content: string) => {
    console.log('Store sendMessage called:', { conversationId, content });
    set({ isSending: true, error: null });

    // Add optimistic user message
    const userMessage: Message = {
      id: `temp-${Date.now()}`,
      conversationId,
      role: 'user',
      content,
      tokenCount: 0,
      createdAt: new Date(),
    };

    set((state) => ({
      messages: {
        ...state.messages,
        [conversationId]: [...(state.messages[conversationId] || []), userMessage],
      },
      streamingMessage: {
        conversationId,
        content: '',
        isStreaming: true,
      },
    }));

    // Send via WebSocket
    websocketService.sendMessage(conversationId, content);
  },

  // Add message to state (for WebSocket updates)
  addMessage: (conversationId: string, message: Message) => {
    set((state) => ({
      messages: {
        ...state.messages,
        [conversationId]: [...(state.messages[conversationId] || []), message],
      },
    }));
  },

  // Append content to streaming message
  appendToStreamingMessage: (conversationId: string, content: string) => {
    set((state) => {
      if (state.streamingMessage?.conversationId !== conversationId) {
        return state;
      }
      return {
        streamingMessage: {
          ...state.streamingMessage,
          content: state.streamingMessage.content + content,
        },
      };
    });
  },

  // Finalize streaming message and add to messages
  finalizeStreamingMessage: (conversationId: string, messageId: string, tokenCount: number) => {
    const { streamingMessage } = get();

    if (streamingMessage?.conversationId === conversationId) {
      const assistantMessage: Message = {
        id: messageId,
        conversationId,
        role: 'assistant',
        content: streamingMessage.content,
        tokenCount,
        createdAt: new Date(),
      };

      set((state) => ({
        messages: {
          ...state.messages,
          [conversationId]: [...(state.messages[conversationId] || []), assistantMessage],
        },
        streamingMessage: null,
        isSending: false,
      }));
    }
  },

  // Update conversation title locally (after server update)
  updateConversationTitle: (conversationId: string, title: string) => {
    set((state) => ({
      conversations: state.conversations.map((conv) =>
        conv.id === conversationId ? { ...conv, title } : conv
      ),
    }));
  },

  // Clear active conversation (show welcome screen)
  clearActiveConversation: () => {
    set({ activeConversationId: null });
  },

  // Set error
  setError: (error: string | null) => {
    set({ error, isSending: false, streamingMessage: null });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },
}));
